import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/outline';
import {FC, ReactNode, useMemo, useRef} from 'react';

import {ButtonShapes} from '~/modules/Appearance/types';
import {Heading} from '~/modules';
import {Link} from '~/modules/Common/components/Link';
import {Listbox} from '@headlessui/react';
import {MergedProduct} from '../context/types';
import {ShoppingType} from '~/gql/generated';
import {Text} from '~/modules/Common/components/Text';
import clsx from 'clsx';
import {useExpert} from '~/modules/Common/hooks/useExpert';
import {useLocation} from '@remix-run/react';
import {useSearchParamOptions} from '../hooks/useSearchParamOptions';

interface ProductOptionsProps {
  product: MergedProduct;
  isLink?: boolean;
  onClick?: (optionName: string, optionValue: string) => void;
  selectedOptions?: Record<string, string>;
}

export function ProductOptions({
  product,
  isLink = true,
  onClick,
  selectedOptions,
}: ProductOptionsProps) {
  const {searchParamsWithDefaults} = useSearchParamOptions(product);
  const activeOptions = useMemo(() => {
    return (
      selectedOptions || Object.fromEntries(searchParamsWithDefaults) || {}
    );
  }, [selectedOptions, searchParamsWithDefaults]);

  return !product.shoppingType ||
    [ShoppingType.Full, ShoppingType.FirstParty].includes(
      product.shoppingType,
    ) ? (
    <ShopifyProductOptions
      product={product}
      isLink={isLink}
      onClick={onClick}
      selectedOptions={activeOptions}
    />
  ) : (
    <ApiProductOptions
      product={product}
      isLink={isLink}
      onClick={onClick}
      selectedOptions={activeOptions}
    />
  );
}

function ShopifyProductOptions({
  product,
  isLink,
  onClick,
  selectedOptions,
}: ProductOptionsProps) {
  const options = product.brand?.options || [];
  const closeRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex flex-col gap-2">
      {options
        .filter((option) => option.values.length > 1)
        .map((option) => (
          <div
            key={option.name}
            className="flex flex-col flex-wrap gap-y-2 last:mb-0"
          >
            <Heading as="legend" size="copy" className="min-w-[4rem]">
              <strong>{option.name}</strong>
            </Heading>
            <div className="flex flex-wrap items-baseline gap-2">
              {option.values.length > 7 ? (
                <div className="relative w-full">
                  <Listbox>
                    {({open}) => (
                      <>
                        <Listbox.Button
                          ref={closeRef}
                          className={clsx(
                            'flex items-center justify-between w-full py-3 px-4 border border-primary',
                            open
                              ? 'rounded-b md:rounded-t md:rounded-b-none'
                              : 'rounded',
                          )}
                        >
                          <span>{selectedOptions[option.name]}</span>
                          {open ? (
                            <ChevronUpIcon width={20} height={20} />
                          ) : (
                            <ChevronDownIcon width={20} height={20} />
                          )}
                        </Listbox.Button>
                        <Listbox.Options
                          className={clsx(
                            'border-primary bg-contrast absolute bottom-12 z-30 grid h-48 w-full overflow-y-scroll rounded-t border px-2 py-2 transition-[max-height] duration-150 sm:bottom-auto md:rounded-b md:rounded-t-none md:border-t-0 md:border-b',
                            open ? 'max-h-48' : 'max-h-0',
                          )}
                        >
                          {option.values.map((value) => (
                            <Listbox.Option
                              key={`option-${option.name}-${value}`}
                              value={value}
                            >
                              {({active}) => (
                                <ProductOptionLink
                                  isLink={isLink}
                                  optionName={option.name}
                                  optionValue={value}
                                  checked={active}
                                  className={clsx(
                                    'text-primary w-full p-2 transition rounded flex justify-start items-center text-left cursor-pointer',
                                    active && 'bg-primary/10',
                                  )}
                                  selectedOptions={selectedOptions}
                                  onClick={() => {
                                    if (!closeRef?.current) return;
                                    closeRef.current.click();
                                    if (onClick) {
                                      onClick(option.name, value);
                                    }
                                  }}
                                >
                                  {value}
                                  {selectedOptions[option.name] === value && (
                                    <span className="ml-2">
                                      <CheckIcon height={16} width={16} />
                                    </span>
                                  )}
                                </ProductOptionLink>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </>
                    )}
                  </Listbox>
                </div>
              ) : (
                <>
                  {option.values.map((value) => {
                    const checked = selectedOptions[option.name] === value;
                    const id = `option-${option.name}-${value}`;

                    return (
                      <Text key={id}>
                        <ProductOptionLink
                          isLink={isLink}
                          onClick={onClick}
                          optionName={option.name}
                          optionValue={value}
                          selectedOptions={selectedOptions}
                          checked={checked}
                          className={clsx(
                            'leading-none block p-1.5 border-[1.5px] text-sm cursor-pointer transition-all duration-200 rounded-2xl',
                          )}
                        />
                      </Text>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        ))}
    </div>
  );
}

const ApiProductOptions: FC<ProductOptionsProps> = ({
  product,
  isLink,
  onClick,
  selectedOptions,
}) => {
  const countOptions = useMemo(() => {
    return (product.options || []).reduce((acc, {values}) => {
      return acc + values.length;
    }, 0);
  }, [product.options]);

  return countOptions < 2 ? null : (
    <>
      {(product.options || []).map(({key, values}) => {
        return (
          <div
            key={key}
            className="flex flex-col flex-wrap mb-4 gap-y-2 last:mb-0"
          >
            <Heading as="legend" size="copy" className="min-w-[4rem]">
              {key}
            </Heading>
            <div className="flex flex-wrap gap-2">
              {values.map((value) => {
                const checked = selectedOptions[key] === value;
                const id = `option-${key}-${value}`;

                return (
                  <Text key={id}>
                    <ProductOptionLink
                      isLink={isLink}
                      onClick={onClick}
                      optionName={key}
                      optionValue={value}
                      selectedOptions={selectedOptions}
                      checked={checked}
                      className={clsx(
                        'leading-none block p-1.5 border-[1.5px] text-sm cursor-pointer transition-all duration-200 rounded-2xl',
                      )}
                    />
                  </Text>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

const shapeClass = {
  [ButtonShapes.circular]: 'rounded-full',
  [ButtonShapes.square]: 'rounded-none',
  [ButtonShapes.rounded]: 'rounded-md',
};

function ProductOptionLink({
  optionName,
  optionValue,
  selectedOptions,
  children,
  isLink,
  onClick = () => {},
  checked,
  ...props
}: {
  optionName: string;
  optionValue: string;
  selectedOptions: Record<string, string>;
  children?: ReactNode;
  isLink?: boolean;
  onClick?: (optionName: string, optionValue: string) => void;
  checked?: boolean;
  [key: string]: any;
}) {
  const {pathname} = useLocation();
  const searchParams = new URLSearchParams(selectedOptions || '');
  searchParams.set(optionName, optionValue);
  const {theme} = useExpert();
  const shape = theme?.v2?.buttonShape || ButtonShapes.rounded;
  const borderColor = theme?.v2?.buttonColor
    ? theme?.v2?.buttonColor
    : checked
    ? theme.palette?.primary
    : '#555';
  const fontColor =
    theme?.v2?.buttonFontColor || theme.palette?.primary || '#000';
  const buttonColor = checked
    ? theme?.v2?.buttonColor || '#fff'
    : 'transparent';

  return isLink ? (
    <Link
      {...props}
      preventScrollReset
      prefetch="intent"
      replace
      to={`${pathname}?${searchParams.toString()}`}
      className={`${props.className || ''} ${shapeClass[shape] || ''}`}
      style={{
        borderColor,
        color: checked ? fontColor : borderColor,
        backgroundColor: buttonColor,
      }}
    >
      {children ?? optionValue}
    </Link>
  ) : (
    <button {...props} onClick={() => onClick(optionName, optionValue)}>
      {children ?? optionValue}
    </button>
  );
}
